import { useMemo, useRef } from 'react';
import { ColDef, FilterChangedEvent, RowSelectedEvent, ModuleRegistry, ICellRendererParams }
  from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { ReplaySubject } from 'rxjs';
import { useParams } from 'react-router-dom';
import { RowGroupingModule } from 'ag-grid-enterprise';

import AgGridCellRenderer from '@/components/common/TableGrid/CustomComponents/AgGridCellRenderer/AgGridCellRenderer';
import AgGridDatepickerTag4Editor
  from '@/components/common/TableGrid/CustomComponents/AgGridDatepicker/AgGridDatepickerTag4Editor';
import AgGridDropdownEditor from '@/components/common/TableGrid/CustomComponents/AgGridDropdown/AgGridDropdownEditor';
import AgGridProductDropdownEditorAsync
  from '@/components/common/TableGrid/CustomComponents/AgGridDropdown/AgGridProductDropdownEditorAsync';
import AgGridSelectableFilter
  from '@/components/common/TableGrid/CustomComponents/AgGridSelectableFilter/AgGridSelectableFilter';
import { TableGrid } from '@/components/common/TableGrid/TableGrid';
import NoRowsOverlay from '../NoRowsOverlay/NoRowsOverlay';

import {
  SelectionEvent,
  SelectionEventInitiator,
  useGridDataRefresh,
  useGridSelectionSubscription
} from '@/pages/ContractLines/hooks/useGridSelectionSubscription';
import { ContractLineSelectionPayload, clearLinesSelection, lineRefreshObservable, linesSelectionObservable } from
  './linesSelectionObservable';
import { ContractLineBillingCycleOutput, VariableContractLineOutput } from '@/graphql/contracts/_generated_/contracts';
import { variableLineRefreshObservable, variableLinesSelectionObservable } from './variableLinesSelectionObservable';
import AgGridCustomerDropdownEditorAsync from
  '@/components/common/TableGrid/CustomComponents/AgGridDropdown/AgGridCustomerDropdownEditorAsync';

import AgGridPurchaseOrderDropdownEditorAsync
  from '@/components/common/TableGrid/CustomComponents/AgGridDropdown/AgGridPurchaseOrderDropdownEditorAsync';
import BundleGroupingCellRenderer from '../../grid/BundleGroupingCellRenderer';

ModuleRegistry.registerModules([RowGroupingModule]);

interface IContractLinesGridProps {
  contractLines: ContractLineBillingCycleOutput[] | VariableContractLineOutput[];
  columns: ColDef[];
  obsarvable: ReplaySubject<SelectionEvent<ContractLineSelectionPayload>>;
  currency?: string;
}

export default function ContractLinesGrid({
  contractLines,
  columns,
  obsarvable,
  currency
}: IContractLinesGridProps) {
  const gridRef = useRef<AgGridReact>(null);
  const { contractId }: { contractId: UUID } = useParams();
  const contractIdDependency = useMemo(() => [contractId], [contractId]);

  const onFilterChanged = (event: FilterChangedEvent) => {
    clearLinesSelection();
    gridRef.current?.api?.paginationGoToPage(0);

    if (event.api.getDisplayedRowCount() > 0) {
      setTimeout(() => event?.api.hideOverlay());
    }
    else {
      setTimeout(() => event?.api.showNoRowsOverlay());
    }
  };

  useGridDataRefresh({
    gridInstance: gridRef,
    observable: lineRefreshObservable
  });

  useGridDataRefresh({
    gridInstance: gridRef,
    observable: variableLineRefreshObservable
  });

  useGridSelectionSubscription({
    gridInstance: gridRef,
    observable: variableLinesSelectionObservable,
    dependenciesToInvalidateSelection: contractIdDependency
  });

  useGridSelectionSubscription({
    gridInstance: gridRef,
    observable: linesSelectionObservable,
    dependenciesToInvalidateSelection: contractIdDependency
  });

  const getContractLineID = (cl: ContractLineBillingCycleOutput | VariableContractLineOutput) => {
    if ('iD' in cl) return cl?.iD;
    return cl?.id;
  };

  const onRowSelected = ({ api }: RowSelectedEvent<ContractLineBillingCycleOutput | VariableContractLineOutput>) => {
    obsarvable.next({
      initiator: SelectionEventInitiator.GRID,
      selectedGridRows: api.getSelectedRows().map((cl) => ({
        id: getContractLineID(cl),
        payload: {
          contractLine: cl
        }
      }))
    });
  };

  const groupRowRendererParams = {
    innerRenderer: BundleGroupingCellRenderer,
    innerRendererParams: (params: ICellRendererParams) => {
      const firstGroupedRowData = params.value ? params.node.allLeafChildren?.[0]?.data : null;
      return firstGroupedRowData ? {
        data: {
          bundleId: firstGroupedRowData.bundleId ?? 'TestBundleID',
          bundleDescription: firstGroupedRowData.bundleDescription,
          bundleSoftwareCompany: firstGroupedRowData.bundleSoftwareCompany,
          bundleHideInvoiceLines: firstGroupedRowData.bundleHideInvoiceLines,
          bundleHideInvoiceValues: firstGroupedRowData.bundleHideInvoiceValues,
          currency: currency
        }
      } : {
        data: { currency: currency }
      };
    },
    suppressCount: true,
  };

  return (
    <>
      <div style={{
        height: `${contractLines.length > 4 ? 400 : 250}px`,
      }}>
        <TableGrid<ContractLineBillingCycleOutput | VariableContractLineOutput>
          ref={gridRef}
          getRowId={(params) => getContractLineID(params.data)}
          suppressRowClickSelection={true}
          rowData={contractLines}
          columnDefs={columns}
          rowSelection="multiple"
          pagination={false}
          suppressPaginationPanel={true}
          onFilterChanged={onFilterChanged}
          onRowSelected={onRowSelected}
          domLayout="normal"
          components={{
            DropdownEditor: AgGridDropdownEditor,
            ProductDropdownEditorAsync: AgGridProductDropdownEditorAsync,
            CustomerDropdownEditorAsync: AgGridCustomerDropdownEditorAsync,
            PurchaseOrderDropdownEditorAsync: AgGridPurchaseOrderDropdownEditorAsync,
            DatepickerEditor: AgGridDatepickerTag4Editor,
            CellRenderer: AgGridCellRenderer,
            SelectableFilter: AgGridSelectableFilter,
          }}
          noRowsOverlayComponent={NoRowsOverlay}
          defaultColDef={{
            resizable: true,
            sortable: true,
            filter: 'agTextColumnFilter',
            filterParams: {
              buttons: ['apply', 'reset'],
              maxNumConditions: 1,
            },
          }}
          context={{}}
          groupDisplayType={'groupRows'}
          groupDefaultExpanded={1}
          groupRowRendererParams={groupRowRendererParams}
        />
      </div>
    </>
  );
}
